import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import AddPromotionCycle from "./AddPromotionCycle";
import GetAllPromotionCycles from "./GetAllPromotionCycles";
import { Col, Row, Form } from "antd";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const PromotionCycle = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);

	const [cyclesList, setCyclesList] = useState(() => {
		// Try to load from localStorage first
		const storedCycles = localStorage.getItem("promotionCycles");
		if (storedCycles) {
			try {
				return JSON.parse(storedCycles);
			} catch (error) {
				console.error("Error parsing cycles from localStorage:", error);
			}
		}
		// Default dummy data
		const now = dayjs();
		return [
			{
				id: 1,
				cycleName: "Q1 2024 Promotion Cycle",
				cycleType: "Quarterly",
				startDate: now.subtract(90, "day").format("YYYY-MM-DD"),
				endDate: now.subtract(30, "day").format("YYYY-MM-DD"),
				announcementDate: now.subtract(100, "day").format("YYYY-MM-DD"),
				status: "Completed",
				budgetAllocation: 500000,
				eligibleEmployees: 25,
				minTenureMonths: 12,
				minPerformanceRating: 4.0,
				requiredSkillsCount: 5,
				promotionCriteria: "Minimum 12 months tenure, performance rating above 4.0, completion of required training programs, positive feedback from managers",
				notes: "Successfully completed Q1 promotions. 18 employees promoted across various departments.",
			},
			{
				id: 2,
				cycleName: "Q2 2024 Promotion Cycle",
				cycleType: "Quarterly",
				startDate: now.subtract(60, "day").format("YYYY-MM-DD"),
				endDate: now.format("YYYY-MM-DD"),
				announcementDate: now.subtract(70, "day").format("YYYY-MM-DD"),
				status: "Active",
				budgetAllocation: 600000,
				eligibleEmployees: 30,
				minTenureMonths: 12,
				minPerformanceRating: 4.0,
				requiredSkillsCount: 5,
				promotionCriteria: "Minimum 12 months tenure, performance rating above 4.0, completion of required training programs, positive feedback from managers",
				notes: "Currently in progress. Review meetings scheduled for next week.",
			},
			{
				id: 3,
				cycleName: "Annual 2024 Promotion Cycle",
				cycleType: "Annual",
				startDate: now.add(30, "day").format("YYYY-MM-DD"),
				endDate: now.add(120, "day").format("YYYY-MM-DD"),
				announcementDate: now.add(20, "day").format("YYYY-MM-DD"),
				status: "Planning",
				budgetAllocation: 2000000,
				eligibleEmployees: 50,
				minTenureMonths: 18,
				minPerformanceRating: 4.2,
				requiredSkillsCount: 7,
				promotionCriteria: "Minimum 18 months tenure, performance rating above 4.2, leadership skills demonstration, completion of advanced training programs",
				notes: "Planning phase. Budget approved. Criteria under review by management.",
			},
			{
				id: 4,
				cycleName: "Semi-Annual H1 2024",
				cycleType: "Semi-Annual",
				startDate: now.subtract(180, "day").format("YYYY-MM-DD"),
				endDate: now.subtract(90, "day").format("YYYY-MM-DD"),
				announcementDate: now.subtract(190, "day").format("YYYY-MM-DD"),
				status: "Completed",
				budgetAllocation: 1200000,
				eligibleEmployees: 40,
				minTenureMonths: 12,
				minPerformanceRating: 3.8,
				requiredSkillsCount: 4,
				promotionCriteria: "Minimum 12 months tenure, performance rating above 3.8, completion of required training programs",
				notes: "H1 promotions completed successfully. 32 employees promoted.",
			},
			{
				id: 5,
				cycleName: "Special Recognition Q3 2024",
				cycleType: "Ad-hoc",
				startDate: now.add(60, "day").format("YYYY-MM-DD"),
				endDate: now.add(90, "day").format("YYYY-MM-DD"),
				announcementDate: now.add(50, "day").format("YYYY-MM-DD"),
				status: "Planning",
				budgetAllocation: 300000,
				eligibleEmployees: 15,
				minTenureMonths: 6,
				minPerformanceRating: 4.5,
				requiredSkillsCount: 3,
				promotionCriteria: "Exceptional performance, minimum 4.5 rating, outstanding contributions to projects",
				notes: "Special recognition cycle for top performers. Limited to 15 employees.",
			},
		];
	});

	const [isEditMode, setIsEditMode] = useState(false);
	const [currentCycleId, setCurrentCycleId] = useState(null);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
		// Save to localStorage whenever cyclesList changes
		localStorage.setItem("promotionCycles", JSON.stringify(cyclesList));
	}, [dispatch, cyclesList]);

	const handleSubmit = (values) => {
		if (isEditMode && currentCycleId) {
			// Update existing cycle
			setCyclesList((prev) =>
				prev.map((cycle) =>
					cycle.id === currentCycleId
						? { ...values, id: currentCycleId }
						: cycle
				)
			);
			setIsEditMode(false);
			setCurrentCycleId(null);
		} else {
			// Add new cycle
			const newCycle = {
				...values,
				id: Date.now(), // Simple ID generation
			};
			setCyclesList((prev) => [...prev, newCycle]);
		}
		form.resetFields();
	};

	const handleEdit = (record) => {
		setIsEditMode(true);
		setCurrentCycleId(record.id);
		form.setFieldsValue({
			...record,
			startDate: record.startDate ? dayjs(record.startDate) : null,
			endDate: record.endDate ? dayjs(record.endDate) : null,
			announcementDate: record.announcementDate ? dayjs(record.announcementDate) : null,
		});
		// Scroll to form
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	return (
		<div>
			<PageTitle title="Back" />
			<div className="add-promotion-cycle-page-wrapper">
				<Row gutter={[16, 16]} className="add-promotion-cycle-row">
					<Col xs={24} sm={24} md={24} lg={10} xl={10} className="add-promotion-cycle-form-col">
						<AddPromotionCycle form={form} onSubmit={handleSubmit} />
					</Col>
					<Col xs={24} sm={24} md={24} lg={14} xl={14} className="add-promotion-cycle-table-col">
						<div className="promotion-cycle-table-container">
							<GetAllPromotionCycles
								data={cyclesList}
								onEdit={handleEdit}
								onRefresh={() => {}}
							/>
						</div>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default PromotionCycle;

